/**
 * assets/collection.js
 * Loaded only on standard collection pages (sections/collection.liquid).
 * Contains: GzLoadMore, GzFacets, initCollectionStickyNav.
 */

// Back to the top of the page: the arrow under the grid and the Back to top button
const scrollToPageTop = () => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  // Keyboard users carry on from the top of the page, not from the arrow
  document.getElementById('MainContent')?.focus({ preventScroll: true });
};

/**
 * GzLoadMore - AJAX Infinite Loading for Collection Grid
 */
if (!customElements.get('gz-load-more')) {
  class GzLoadMore extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.grid = document.getElementById('product-grid');
      this.isLoading = false;
      // Translated by Liquid: the button's own text and data-label-loading
      this.viewMoreLabel = this.button?.textContent.trim();
      this.loadingLabel = this.dataset.labelLoading || this.viewMoreLabel;
      // Set when the section turns View More into Back to top after the last page
      this.backToTopLabel = this.dataset.labelBackToTop;
    }

    connectedCallback() {
      if (!this.button || !this.grid) return;
      this.button.addEventListener('click', () => {
        if (this.getAttribute('data-next-url')) this.loadMore();
        else scrollToPageTop();
      });
    }

    async loadMore() {
      if (this.isLoading) return;

      const nextUrl = this.getAttribute('data-next-url');
      if (!nextUrl) return;

      this.isLoading = true;
      this.button.classList.add('opacity-50', 'cursor-not-allowed');
      this.button.textContent = this.loadingLabel;

      try {
        const response = await fetch(nextUrl);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newProducts = doc.querySelectorAll('#product-grid > *');
        // The last page renders gz-load-more too when it turns into Back to top, with no next URL
        const followingUrl = doc.querySelector('gz-load-more')?.getAttribute('data-next-url');

        newProducts.forEach(node => this.grid.appendChild(node));

        window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
        window.GZAnalyticsQueue.push(function (GZAnalytics) {
          GZAnalytics.pushViewItemListFromElements(newProducts, 'product listing');
        });

        if (window.gzInitSwipers) window.gzInitSwipers();

        if (followingUrl) {
          this.setAttribute('data-next-url', followingUrl);
          this.button.textContent = this.viewMoreLabel;
        } else if (this.backToTopLabel) {
          // The button takes over from the arrow
          this.removeAttribute('data-next-url');
          this.button.textContent = this.backToTopLabel;
          this.parentElement.querySelector('gz-back-to-top')?.remove();
        } else {
          this.remove();
        }
      } catch (e) {
        console.error('Error loading more products:', e);
        this.button.textContent = this.viewMoreLabel;
      } finally {
        this.isLoading = false;
        this.button.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    }
  }
  customElements.define('gz-load-more', GzLoadMore);
}

/**
 * GzBackToTop - Arrow under the collection grid, back to the top of the page
 */
if (!customElements.get('gz-back-to-top')) {
  class GzBackToTop extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', scrollToPageTop);
    }
  }
  customElements.define('gz-back-to-top', GzBackToTop);
}

/**
 * GzFacets - AJAX Filtering and Sorting
 */
if (!customElements.get('gz-facets')) {
  class GzFacets extends HTMLElement {
    constructor() {
      super();
      this.drawer = this.querySelector('#FacetsDrawer');
      this.form = this.querySelector('#FacetsFilterForm');
      this.grid = document.getElementById('product-grid');
      this.loadMoreContainer = this.querySelector('gz-load-more');
    }

    connectedCallback() {
      this.init();
      this.loadLazySections();
    }

    init() {
      this.querySelectorAll('[data-facets-open]').forEach(btn => {
        btn.onclick = () => {
          const view = btn.getAttribute('data-facets-view') || 'filters';
          if (this.drawer) this.drawer.setAttribute('data-view', view);
          this.toggleDrawer(true);
        };
      });
      this.querySelectorAll('.js-close-facets').forEach(btn => {
        btn.onclick = () => this.toggleDrawer(false);
      });

      // The models nav "All" link is left to _bindSubCollLinks, binding it here too would render twice
      this.querySelectorAll('.js-subcoll-link[href]:not([data-nav-model-all])').forEach(link => {
        link.onclick = (e) => {
          e.preventDefault();
          this.renderPage(link.getAttribute('href'));
        };
      });

      if (this.form) {
        this.form.onsubmit = (e) => {
          e.preventDefault();
          this.onFilterChange();
        };
      }

      this._bindFacetAccordions();
      this._bindSubCollLinks();
    }

    _bindSubCollLinks() {
      if (this.subCollBound) return;
      this.subCollBound = true;

      this.addEventListener('click', (e) => {
        // Models nav only: subcollection cards have their own onclick, handling them here too
        // fired a second renderPage on the current collection that raced the first one
        const link = e.target.closest('.js-subcoll-link');
        if (!link || !link.matches('[data-nav-model-all], [data-filter-param]')) return;

        e.preventDefault();
        const currentUrl = new URL(window.location.href);
        const newParams = new URLSearchParams(currentUrl.search);

        const keysToRemove = [];
        for (const key of newParams.keys()) {
          if (key.includes('model_filter')) keysToRemove.push(key);
        }
        keysToRemove.forEach(k => newParams.delete(k));

        // The model buttons carry the filter pair instead of a ready-made URL
        const isAll = link.hasAttribute('data-nav-model-all');
        if (!isAll && link.dataset.filterParam) {
          newParams.append(link.dataset.filterParam, link.dataset.filterValue || '');
        }

        const searchStr = newParams.toString();
        const finalUrl = `${window.location.pathname}${searchStr ? '?' + searchStr : ''}`;

        this.querySelectorAll('.js-subcoll-link span:first-child').forEach(s => s.classList.remove('underline'));
        this.querySelectorAll('.js-nav-model-count').forEach(c => c.classList.add('hidden'));

        const span = link.querySelector('span:first-child');
        if (span) span.classList.add('underline');

        const count = link.querySelector('.js-nav-model-count');
        if (count) count.classList.remove('hidden');

        this.renderPage(finalUrl);
      });
    }

    _bindFacetAccordions() {
      this.querySelectorAll('[data-facet-accordion]').forEach(btn => {
        btn.onclick = (e) => {
          e.preventDefault();
          const content = btn.nextElementSibling;
          if (!content) return;
          const isOpen = content.getAttribute('aria-hidden') === 'false';
          const icon = btn.querySelector('.js-icon');

          if (isOpen) {
            content.setAttribute('aria-hidden', 'true');
            btn.setAttribute('aria-expanded', 'false');
            if (icon) icon.style.transform = 'rotate(0deg)';
          } else {
            content.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
            if (icon) icon.style.transform = 'rotate(180deg)';
          }
        };
      });
    }

    toggleDrawer(open) {
      if (!this.drawer) return;
      const overlay = this.drawer.querySelector('.js-close-facets');
      const inner = this.drawer.querySelector('.absolute.right-0');

      if (open) {
        this.drawer.classList.remove('invisible', 'pointer-events-none');
        this.drawer.classList.add('pointer-events-auto');

        if (window.FocusTrap && !this.focusTrap) {
          this.focusTrap = new FocusTrap(this.drawer, {
            onEscape: () => this.toggleDrawer(false)
          });
        }

        setTimeout(() => {
          overlay.classList.add('opacity-100');
          inner.classList.remove('translate-x-full');
          if (this.focusTrap) this.focusTrap.activate();
        }, 10);
      } else {
        overlay.classList.remove('opacity-100');
        inner.classList.add('translate-x-full');
        this.drawer.classList.remove('pointer-events-auto');
        this.drawer.classList.add('pointer-events-none');
        if (this.focusTrap) this.focusTrap.deactivate();
        setTimeout(() => this.drawer.classList.add('invisible'), 300);
      }
    }

    async onFilterChange() {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData).toString();
      const url = `${window.location.pathname}?${searchParams}`;
      this.toggleDrawer(false);
      this.renderPage(url);
    }

    async renderPage(url) {
      this.classList.add('is-loading');
      this.grid.style.opacity = '0.5';

      try {
        const response = await fetch(url);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newGrid = doc.getElementById('product-grid');
        const currentGrid = document.getElementById('product-grid');
        if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;

        const newFilters = doc.getElementById('AnalyticsActiveFilters');
        const currentFilters = document.getElementById('AnalyticsActiveFilters');
        if (newFilters && currentFilters) currentFilters.textContent = newFilters.textContent;

        if (currentGrid) {
          const newListName = currentGrid.querySelector('[data-item-list-name]')?.dataset.itemListName || 'filtered product listing';
          window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
          window.GZAnalyticsQueue.push(function (GZAnalytics) {
            GZAnalytics.pushViewItemListFromContainer('#product-grid', newListName);
          });
        }

        const newCounts = doc.querySelectorAll('[data-nav-element="product-count"]');
        const currentCounts = document.querySelectorAll('[data-nav-element="product-count"]');
        currentCounts.forEach((count, index) => {
          if (newCounts[index]) count.innerHTML = newCounts[index].innerHTML;
        });

        // Arrow, View More and Back to top as the filtered page renders them
        const newListEnd = doc.getElementById('CollectionListEnd');
        const currentListEnd = document.getElementById('CollectionListEnd');
        if (newListEnd && currentListEnd) {
          currentListEnd.replaceWith(newListEnd);
        } else if (newListEnd) {
          this.grid.parentElement.appendChild(newListEnd);
        } else if (currentListEnd) {
          currentListEnd.remove();
        }

        const selectorsToUpdate = [
          '#CollectionBreadcrumbs',
          '#CollectionIntroImageWrapper',
          '#CollectionDescriptionShortWrapper',
          '#CollectionDescriptionExpandableWrapper',
          '.flex.items-center.gap-6.lg\\:gap-8',
          '.sub-nav-container',
          '.toolbar .font-medium',
          '#FacetsDrawer',
          '#models-nav-container'
        ];

        selectorsToUpdate.forEach(selector => {
          const newEl = doc.querySelector(selector);
          const currentEl = this.querySelector(selector);
          if (newEl && currentEl) {
            if (selector === '.sub-nav-container') {
              if (newEl.getAttribute('data-parent-handle') !== currentEl.getAttribute('data-parent-handle')) {
                currentEl.innerHTML = newEl.innerHTML;
                currentEl.setAttribute('data-loaded', 'false');
                currentEl.setAttribute('data-parent-handle', newEl.getAttribute('data-parent-handle'));
              }
            } else {
              currentEl.innerHTML = newEl.innerHTML;
            }
          }
        });

        if (typeof initExpandableText === 'function') initExpandableText();

        this.form = this.querySelector('#FacetsFilterForm');
        if (this.init) {
          this.init();
          initCollectionStickyNav();
        }

        history.pushState({ url }, '', url);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.loadLazySections();
        this.updateActiveStates(url);

        if (typeof initExpandableText === 'function') initExpandableText();
      } catch (e) {
        console.error('Filtering failed:', e);
      } finally {
        this.classList.remove('is-loading');
        this.grid.style.opacity = '1';
      }
    }

    updateActiveStates(url) {
      const pathname = new URL(url, window.location.origin).pathname;

      this.querySelectorAll('.js-subcoll-link').forEach(link => {
        const linkPath = new URL(link.getAttribute('href'), window.location.origin).pathname;
        const container = link.querySelector('div');
        const text = link.querySelector('span');

        if (linkPath === pathname) {
          container?.classList.add('border-black');
          container?.classList.remove('border-transparent');
          text?.classList.add('text-black');
        } else {
          container?.classList.remove('border-black');
          container?.classList.add('border-transparent');
        }
      });

      this.querySelectorAll('.toolbar .flex-1 a').forEach(link => {
        const linkPath = new URL(link.getAttribute('href'), window.location.origin).pathname;
        if (linkPath === pathname) {
          link.classList.add('font-bold', 'border-b-2', 'border-black');
          link.classList.remove('text-gray-400');
        } else {
          link.classList.remove('font-bold', 'border-b-2', 'border-black');
          link.classList.add('text-gray-400');
        }
      });
    }

    async loadLazySections() {
      const isDesktop = window.matchMedia('(min-width: 1024px)');
      const lazyElements = this.querySelectorAll('[data-lazy-section]');

      const performLoad = async (el) => {
        if (el.dataset.loaded === 'true') return;
        const sectionId = el.getAttribute('data-lazy-section');
        const url = `${window.location.pathname}?section_id=${sectionId}`;

        try {
          const response = await fetch(url);
          const html = await response.text();
          if (html && html.trim() !== '') {
            el.innerHTML = html;
            el.dataset.loaded = 'true';
            this.initSubcollLinks(el);
            const inner = el.firstElementChild;
            if (inner) {
              inner.classList.add('opacity-0', 'transition-opacity', 'duration-700');
              setTimeout(() => inner.classList.add('opacity-100'), 50);
            }
          } else {
            el.style.display = 'none';
          }
        } catch (e) {
          console.error(`Failed to load lazy section ${sectionId}:`, e);
          el.style.display = 'none';
        }
      };

      const checkAndLoad = () => {
        if (isDesktop.matches) {
          lazyElements.forEach(performLoad);
        } else {
          lazyElements.forEach(el => {
            if (el.dataset.loaded !== 'true') el.style.display = 'none';
          });
        }
      };

      checkAndLoad();
      isDesktop.addEventListener('change', (e) => {
        if (e.matches) {
          lazyElements.forEach(el => el.style.display = '');
          checkAndLoad();
        }
      });
    }

    initSubcollLinks(container = this) {
      container.querySelectorAll('.js-subcoll-link').forEach(link => {
        link.onclick = (e) => {
          e.preventDefault();
          this.renderPage(link.getAttribute('href'));
        };
      });
    }
  }
  customElements.define('gz-facets', GzFacets);
}

/**
 * Collection Sticky Navigation Manager
 */
const initCollectionStickyNav = () => {
  const container = document.querySelector('.collection-page[data-sticky-container], .collection-editorial-page');
  if (!container) return;

  const editorialSections = container.querySelectorAll('[data-sticky-section]');

  if (editorialSections.length > 0 && typeof window.initEditorialStickyNav === 'function') {
    window.initEditorialStickyNav();
    return;
  }

  const nav = container.querySelector('[data-sticky-nav]');
  const toolbar = container.querySelector('.toolbar-sticky-wrapper');
  if (!nav || !toolbar) {
    container.classList.add('nav-ready');
    return;
  }

  container.querySelectorAll('.js-sentinel').forEach(s => s.remove());

  const navSentinel = document.createElement('div');
  navSentinel.className = 'js-sentinel js-nav-sentinel';
  navSentinel.style.height = '1px';
  container.prepend(navSentinel);

  const toolbarSentinel = document.createElement('div');
  toolbarSentinel.className = 'js-sentinel js-toolbar-sentinel absolute top-0 w-full pointer-events-none';
  toolbarSentinel.style.height = '1px';
  container.appendChild(toolbarSentinel);

  const navObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      container.classList.toggle('is-sticky', !entry.isIntersecting);
    });
  }, { threshold: [0], rootMargin: '0px' });

  const updateToolbarObserver = () => {
    if (window.collectionToolbarObserver) {
      window.collectionToolbarObserver.disconnect();
    }
    const navHeight = nav.getBoundingClientRect().height || 80;
    window.collectionToolbarObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const isMerged = !entry.isIntersecting;
        container.classList.toggle('is-merged', isMerged);

        // Force transparency via JS to completely bypass any Tailwind caching/specificity issues.
        // Transparency only makes sense while .collection-sticky-nav-wrapper (bg-white) is behind
        // it providing the backdrop - when that wrapper is hidden (e.g. no subcategories/models,
        // lg:hidden), the toolbar must stay opaque or product images show through it.
        const toolbarDiv = container.querySelector('.toolbar-sticky-wrapper > div');
        const navProvidesBackdrop = nav.offsetHeight > 0;
        if (toolbarDiv) {
          if (isMerged && window.innerWidth >= 1024 && navProvidesBackdrop) {
            toolbarDiv.style.backgroundColor = 'transparent';
          } else {
            toolbarDiv.style.backgroundColor = '#fff';
          }
        }
      });
    }, { threshold: [0], rootMargin: `-${navHeight - 2}px 0px 0px 0px` });
    window.collectionToolbarObserver.observe(toolbarSentinel);
  };

  navObserver.observe(navSentinel);
  updateToolbarObserver();

  if (window.collectionToolbarResizeHandler) {
    window.removeEventListener('resize', window.collectionToolbarResizeHandler);
  }
  window.collectionToolbarResizeHandler = updateToolbarObserver;
  window.addEventListener('resize', window.collectionToolbarResizeHandler);

  // Sentinels/observers are set up - reveal the nav now so there's no visible layout shift.
  container.classList.add('nav-ready');
};

document.addEventListener('DOMContentLoaded', () => {
  initCollectionStickyNav();

  if (window.ThemeConfig?.isDesignMode) {
    document.addEventListener('shopify:section:load', () => {
      initCollectionStickyNav();
    });
  }
});

// Bridge for cross-file Sticky re-init
window.initCollectionStickyNav = initCollectionStickyNav;
